import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
// [A형] ParagraphSelector/SectionSelector(2단계) → EntrySelector(한 화면 통합)로 교체.
// 두 원본 파일은 롤백 대비 레포에 보존한다.
import EntrySelector from "../features/assignment/components/common/EntrySelector";

import {
  fetchParagraphList,
  fetchSectionList,
  setParagraph,
  setSection,
  setSectionList,
  setParagraphList,
  setAssignment,
  retreiveAssignment,
} from "../features/assignment/assignmentSlice";
import {
  buildDemoAssignment,
  buildDemoParagraphList,
  buildDemoSectionList,
} from "../features/assignment/demoDirectTranslation";
import { loadDemoContent } from "../features/assignment/demoContent";
import TopBar from "../features/assignment/components/widgets/TopBar";
import SectionPage from "../features/assignment/components/common/SectionPage";
import { checkUser } from "../features/student/studentSlice";
import "./StudyPage.css";

const PreviewPage = () => {
  const dispatch = useDispatch();
  const { paragraphList, sectionList } = useSelector(
    (state) => state.assignment
  );
  const [selectedParagraph, setSelectedParagraph] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  // [레이스 수정] SPA에서 과제 A→B 연속 진입 시 redux에 A의 paragraphList가 남아
  // 있는데, EntrySelector 자동선택이 그 낡은 목록을 집어 B 화면에 A의 섹션이
  // 뜨는 버그가 있었다("내용이해 메뉴가 모의고사에 나옴"). 이번 과제의
  // fetchParagraphList가 끝난 뒤에만 진입 화면을 활성화한다.
  const [entryReady, setEntryReady] = useState(false);
  // 체험 키가 있으면 서버 없이 번들에 심은 데이터로 그린다 (demoContent.js).
  const demoKey = new URLSearchParams(window.location.search).get("demo");
  const isDemo = Boolean(demoKey);
  // 지문이 여럿인 체험(단어장 1~100 / 101~110 등)에서 지문을 고를 때 바꿔 끼울
  // 지문별 섹션 목록. 서버를 부르지 않으므로 여기 들고 있는다.
  const demoSectionMapRef = useRef(null);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const assignmentId = queryParams.get("id");

    const session = queryParams.get("session");

    // **로그인 없이 열리는 체험 모드.** 가입하지 않은 선생님에게 링크 하나만 보내
    // 실제 학생 화면을 그대로 눌러보게 하려는 것이다. 세션을 URL 에 실어 돌리면
    // 그 계정의 열쇠가 되어 받은 사람마다 같은 학생 기록에 겹쳐 쓰므로, 여기서는
    // 아예 서버를 부르지 않고 화면 안에 심어 둔 값으로 그린다.
    if (queryParams.get("demo") === "direct-translation") {
      dispatch(setAssignment(buildDemoAssignment()));
      const demoParagraphList = buildDemoParagraphList();
      const demoParagraph = demoParagraphList[0];
      dispatch(setParagraphList(demoParagraphList));
      dispatch(setParagraph(demoParagraph));
      dispatch(setSectionList(buildDemoSectionList()));
      setSelectedParagraph(demoParagraph);
      setEntryReady(true);
      return;
    }

    // 실제 체험 과제에서 떠 온 데이터로 여는 체험 (지문분석·내용이해·모의고사·단어 등)
    if (queryParams.get("demo")) {
      loadDemoContent(queryParams.get("demo"))
        .then((content) => {
          if (!content) {
            alert("잘못된 URL입니다.");
            return;
          }
          dispatch(setAssignment(content.assignment));
          dispatch(setParagraphList(content.paragraph_list));
          demoSectionMapRef.current = content.section_map;
          const firstParagraph = content.paragraph_list[0];
          dispatch(setParagraph(firstParagraph));
          dispatch(setSectionList(content.section_map[firstParagraph.id] || []));
          setSelectedParagraph(firstParagraph);
          setEntryReady(true);
        })
        .catch(() => {
          alert("잘못된 URL입니다.");
        });
      return;
    }

    if (session) {
      try {
        const decoded = atob(session);
        const [id, randomCode] = decoded.split(":");
        dispatch(checkUser({ userId: id, randomCode: randomCode }));

        if (assignmentId) {
          try {
            dispatch(retreiveAssignment(assignmentId));
            dispatch(fetchParagraphList(assignmentId)).then(() => setEntryReady(true));
          } catch (error) {
            alert("잘못된 URL입니다.");
          }
        } else {
          alert("잘못된 URL입니다.");
        }
      } catch (error) {
        alert("잘못된 URL입니다.");
      }
    } else {
      alert("잘못된 URL입니다.");
    }
  }, [dispatch]);

  // [A형] 지문 행 클릭 = 기존 "다음" 버튼(handleCompleteParagraphSelection)의
  // 몸통(setParagraph + fetchSectionList)을 그대로 클릭 시점으로 이동.
  const handleSelectParagraph = (paragraph) => {
    setSelectedParagraph(paragraph);
    dispatch(setParagraph(paragraph));
    // 체험은 섹션을 이미 심어 두었다. 서버를 부르면 없는 지문을 찾다가 비워진다.
    if (isDemo) {
      const sectionMap = demoSectionMapRef.current;
      if (sectionMap) dispatch(setSectionList(sectionMap[paragraph.id] || []));
      return;
    }
    dispatch(fetchSectionList(paragraph.id));
  };

  // [A형] 섹션 행 클릭 = 기존 "학습하기" 버튼(handleCompleteSectionSelection)의
  // 몸통(setSection + setCurrentStep(3))을 그대로 클릭 시점으로 이동.
  const handleEnterSection = (section) => {
    dispatch(setSection(section));
    setSelectedSection(null);
    setCurrentStep(3);
  };

  const renderContent = () => {
    // [A형] step 1과 2를 모두 EntrySelector로 렌더한다.
    // 각 학습 화면은 완료 시 setCurrentStep(2)로 "섹션 선택 복귀"를 호출하는데,
    // 2를 함께 매핑하면 그 호출들이 그대로 통합 진입 화면 복귀가 되어
    // 학습 컴포넌트 수정이 0개가 된다.
    if (currentStep === 1 || currentStep === 2) {
      return (
        <EntrySelector
          entryReady={entryReady}
          paragraphList={paragraphList}
          sectionList={sectionList}
          selectedParagraph={selectedParagraph}
          onSelectParagraph={handleSelectParagraph}
          onEnterSection={handleEnterSection}
          isPreview={true}
        />
      );
    }
    return <SectionPage setCurrentStep={setCurrentStep} isPreview={true} />;
  };

  return (
    <div
      className="study-page"
      onContextMenu={(e) => {
        e.preventDefault();
      }}
    >
      <div className="study-page__column">
        <TopBar
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          isPreview={true}
        />
        {renderContent()}
      </div>
    </div>
  );
};

export default PreviewPage;
